import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Image, 
  Layers, 
  User, 
  LogOut, 
  Upload,
  X,
  BookOpen,
  Save,
  Crop,
  ShoppingBag
} from 'lucide-react';
import { getApiUrl, makeRequest, resolveImageUrl } from '../../utils/api';
import ImageCropper from '../../components/ImageCropper';
import '../../styles/admin.css';

const AdminBusiness = () => {
  const { token, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [cropExistingImage, setCropExistingImage] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    tagline: '',
    bio_title_1: '',
    bio_title_2: '',
    bio_text_1: '',
    bio_text_2: '',
    profile_image: '',
    phone: '',
    email: '',
    instagram: '',
    baldwin_booking_url: '',
    islandia_booking_url: '',
    amazon_storefront_url: '',
    mac_storefront_url: '',
    ae_storefront_url: ''
  });

  const fetchBusinessInfo = useCallback(async () => {
    try {
      const apiUrl = getApiUrl();
      const result = await makeRequest('GET', `${apiUrl}/api/business-info`);
      if (result.ok) {
        const data = result.data;
        setFormData({
          name: data.name || '',
          tagline: data.tagline || '',
          bio_title_1: data.bio_title_1 || '',
          bio_title_2: data.bio_title_2 || '',
          bio_text_1: data.bio_text_1 || '',
          bio_text_2: data.bio_text_2 || '',
          profile_image: data.profile_image || '',
          phone: data.phone || '',
          email: data.email || '',
          instagram: data.instagram || '',
          baldwin_booking_url: data.baldwin_booking_url || '',
          islandia_booking_url: data.islandia_booking_url || '',
          amazon_storefront_url: data.amazon_storefront_url || ''
        });
      }
    } catch (error) {
      console.error('Error fetching business info:', error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchBusinessInfo();
  }, [fetchBusinessInfo]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const apiUrl = getApiUrl();
      const result = await makeRequest('POST', `${apiUrl}/api/upload/image`, uploadData, {
        'Authorization': `Bearer ${token}`
      }, true);

      if (result.ok) {
        setFormData(prev => ({ ...prev, profile_image: result.data.url }));
      } else {
        alert('Error uploading image: ' + (result.data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error uploading image: ' + error.message);
    }
    setUploading(false);
  };

  const handleCroppedImageUpload = async (croppedBlob) => {
    setUploading(true);
    setShowCropper(false);
    setCropExistingImage(null);
    
    const uploadData = new FormData();
    uploadData.append('file', croppedBlob, 'cropped-image.jpg');

    try {
      const apiUrl = getApiUrl();
      const result = await makeRequest('POST', `${apiUrl}/api/upload/image`, uploadData, {
        'Authorization': `Bearer ${token}`
      }, true);

      if (result.ok) {
        setFormData(prev => ({ ...prev, profile_image: result.data.url }));
      } else {
        alert('Error uploading image: ' + (result.data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error uploading image: ' + error.message);
    }
    setUploading(false);
  };

  const handleCropExisting = () => {
    // Open cropper with the current image URL
    setCropExistingImage(resolveImageUrl(formData.profile_image));
    setShowCropper(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const apiUrl = getApiUrl();
      const result = await makeRequest('PUT', `${apiUrl}/api/business-info`, formData, {
        'Authorization': `Bearer ${token}`
      });

      if (result.ok) {
        alert('Business info saved successfully!');
      } else {
        alert('Error saving business info: ' + (result.data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error saving business info: ' + error.message);
    }
    setSaving(false);
  };

  return (
    <div className="admin-layout">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="admin-sidebar-header">
          <h2>The Slay Station</h2>
          <p>Admin Panel</p>
        </div>

        <nav className="admin-nav">
          <Link to="/admin/dashboard" className="admin-nav-item">
            <LayoutDashboard size={20} />
            <span>Dashboard</span>
          </Link>
          <Link to="/admin/portfolio" className="admin-nav-item">
            <Image size={20} />
            <span>Portfolio</span>
          </Link>
          <Link to="/admin/services" className="admin-nav-item">
            <Layers size={20} />
            <span>Services</span>
          </Link>
          <Link to="/admin/storefront" className="admin-nav-item">
            <ShoppingBag size={20} />
            <span>Curated Favorites</span>
          </Link>
          <Link to="/admin/business" className="admin-nav-item active">
            <User size={20} />
            <span>Bio & Info</span>
          </Link>
        </nav>

        <div className="admin-sidebar-footer">
          <Link to="/" className="admin-nav-item" target="_blank">
            <BookOpen size={20} />
            <span>View Site</span>
          </Link>
          <button onClick={logout} className="admin-nav-item logout">
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <header className="admin-header">
          <div>
            <h1>Bio & Business Info</h1>
            <p>Update your profile and contact information</p>
          </div>
          <button onClick={handleSubmit} disabled={saving} className="admin-btn-primary">
            <Save size={20} />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </header>

        <div className="admin-content">
          {loading ? (
            <div className="admin-loading">Loading...</div>
          ) : (
            <form onSubmit={handleSubmit} className="admin-business-form">
              {/* Profile Image Section */}
              <div className="admin-section-card">
                <h2>Profile Image</h2>
                <div className="admin-profile-upload">
                  {formData.profile_image ? (
                    <div className="admin-profile-preview">
                      <img src={resolveImageUrl(formData.profile_image)} alt="Profile" />
                      <div className="admin-image-actions">
                        <button 
                          type="button" 
                          onClick={handleCropExisting}
                          className="admin-image-crop"
                          title="Crop Image"
                        >
                          <Crop size={16} />
                        </button>
                        <button 
                          type="button" 
                          onClick={() => setFormData(prev => ({ ...prev, profile_image: '' }))}
                          className="admin-image-remove"
                          title="Remove Image"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="admin-upload-options">
                      <button
                        type="button"
                        onClick={() => {
                          setCropExistingImage(null);
                          setShowCropper(true);
                        }}
                        className="admin-crop-upload-btn"
                        disabled={uploading}
                      >
                        <Crop size={24} />
                        <span>Crop & Upload</span>
                      </button>
                      <span className="admin-upload-divider">or</span>
                      <label className="admin-profile-upload-btn">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          hidden
                        />
                        {uploading ? (
                          <span>Uploading...</span>
                        ) : (
                          <>
                            <Upload size={24} />
                            <span>Upload Without Crop</span>
                          </>
                        )}
                      </label>
                    </div>
                  )}
                </div>
                <p className="admin-form-hint">Or paste an image URL:</p>
                <input
                  type="url"
                  value={formData.profile_image}
                  onChange={(e) => setFormData(prev => ({ ...prev, profile_image: e.target.value }))}
                  placeholder="https://..."
                  className="admin-full-width"
                />
              </div>

              {/* Bio Section */}
              <div className="admin-section-card">
                <h2>Bio Information</h2>
                <div className="admin-form-row">
                  <div className="admin-form-group">
                    <label>Title Line 1</label>
                    <input
                      type="text"
                      value={formData.bio_title_1}
                      onChange={(e) => setFormData(prev => ({ ...prev, bio_title_1: e.target.value }))}
                      placeholder="e.g., Meet Your"
                    />
                  </div>
                  <div className="admin-form-group">
                    <label>Title Line 2</label>
                    <input
                      type="text"
                      value={formData.bio_title_2}
                      onChange={(e) => setFormData(prev => ({ ...prev, bio_title_2: e.target.value }))}
                      placeholder="e.g., Esthetician"
                    />
                  </div>
                </div>

                <div className="admin-form-group">
                  <label>Bio Paragraph 1</label>
                  <textarea
                    value={formData.bio_text_1}
                    onChange={(e) => setFormData(prev => ({ ...prev, bio_text_1: e.target.value }))}
                    placeholder="Your first paragraph..."
                    rows={4}
                  />
                </div>

                <div className="admin-form-group">
                  <label>Bio Paragraph 2</label>
                  <textarea
                    value={formData.bio_text_2}
                    onChange={(e) => setFormData(prev => ({ ...prev, bio_text_2: e.target.value }))}
                    placeholder="Your second paragraph..."
                    rows={4}
                  />
                </div>
              </div>

              {/* Contact Section */}
              <div className="admin-section-card">
                <h2>Contact Information</h2>
                <div className="admin-form-row">
                  <div className="admin-form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="(555) 555-5555"
                    />
                  </div>
                  <div className="admin-form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="email@example.com"
                    />
                  </div>
                </div>

                <div className="admin-form-group">
                  <label>Instagram URL</label>
                  <input
                    type="url"
                    value={formData.instagram}
                    onChange={(e) => setFormData(prev => ({ ...prev, instagram: e.target.value }))}
                    placeholder="https://instagram.com/yourusername"
                  />
                </div>
              </div>

              {/* Booking Links Section */}
              <div className="admin-section-card">
                <h2>Booking Links</h2>
                <div className="admin-form-group">
                  <label>Baldwin Location Booking URL</label>
                  <input
                    type="url"
                    value={formData.baldwin_booking_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, baldwin_booking_url: e.target.value }))}
                    placeholder="https://glossgenius.com/..."
                  />
                </div>

                <div className="admin-form-group">
                  <label>Islandia Location Booking URL</label>
                  <input
                    type="url"
                    value={formData.islandia_booking_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, islandia_booking_url: e.target.value }))}
                    placeholder="https://glossgenius.com/..."
                  />
                </div>
              </div>

              {/* Amazon Storefront */}
              <div className="admin-section-card">
                <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <ShoppingBag size={20} />
                  Storefront Links
                </h2>
                <div className="admin-form-group">
                  <label>Amazon Storefront URL</label>
                  <input
                    type="url"
                    value={formData.amazon_storefront_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, amazon_storefront_url: e.target.value }))}
                    placeholder="https://www.amazon.com/shop/yourname"
                  />
                </div>
                <div className="admin-form-group">
                  <label>MAC Cosmetics Storefront URL</label>
                  <input
                    type="url"
                    value={formData.mac_storefront_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, mac_storefront_url: e.target.value }))}
                    placeholder="https://community.maccosmetics.com/a/yourname"
                  />
                  <p className="admin-form-hint">
                    Manage individual featured products in <Link to="/admin/storefront" style={{ color: '#b8860b' }}>Curated Favorites</Link>.
                  </p>
                </div>
                <div className="admin-form-group">
                  <label>American Eagle Ambassador URL</label>
                  <input
                    type="url"
                    value={formData.ae_storefront_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, ae_storefront_url: e.target.value }))}
                    placeholder="https://www.ae.com/..."
                  />
                </div>
              </div>

              <div className="admin-form-footer">
                <button type="submit" disabled={saving} className="admin-btn-primary large">
                  <Save size={20} />
                  {saving ? 'Saving...' : 'Save All Changes'}
                </button>
              </div>
            </form>
          )}
        </div>
      </main>

      {/* Image Cropper Modal */}
      {showCropper && (
        <ImageCropper
          onCropComplete={handleCroppedImageUpload}
          onCancel={() => {
            setShowCropper(false);
            setCropExistingImage(null);
          }}
          existingImageUrl={cropExistingImage}
          parentToken={token}
        />
      )}
    </div>
  );
};

export default AdminBusiness;
